import React, { useEffect, useState } from 'react';
import {
  X,
  FileSpreadsheet,
  Calendar,
  DollarSign,
  Truck,
  CheckCircle2,
  Clock,
  XCircle,
  FileText,
  Building2,
  MapPin,
  Loader2
} from 'lucide-react';
import { Medicoes } from '../../types/medicao';
import { medicaoService } from '../../services/medicaoService';
import { exportMedicaoToExcel, exportMedicaoToPdf, resolveTipoVeiculoDisplay } from '../../services/medicaoExportService';
import { fixEncodingIssues } from '../../utils/textUtils';
import { formatDateBR } from '../../utils/dateUtils';

interface AreaClienteDetalhesMedicaoModalProps {
  medicao: Medicoes | null;
  onClose: () => void;
}

export function AreaClienteDetalhesMedicaoModal({
  medicao,
  onClose
}: AreaClienteDetalhesMedicaoModalProps) {
  const [osList, setOsList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);

  useEffect(() => {
    if (!medicao) return;
    let isMounted = true;
    setLoading(true);

    medicaoService.getOSByMedicaoCliente(medicao.id)
      .then((data) => {
        if (isMounted) {
          setOsList(data || []);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error('Erro ao buscar OS da medição:', err);
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [medicao]);

  if (!medicao) return null;

  const handleExportExcel = () => {
    if (osList.length === 0) {
      alert('Não há ordens de serviço nesta medição para exportar.');
      return;
    }
    try {
      exportMedicaoToExcel(
        {
          id: medicao.id,
          type: 'cliente',
          clienteNome: medicao.cliente_nome || medicao.pedido?.pessoa?.nome,
          periodoInicial: medicao.periodo_inicial,
          periodoFinal: medicao.periodo_final,
          valorTotal: medicao.valor_medicao,
          statusNome: (medicao as any).status?.status || (medicao as any).status_nome,
        },
        osList.map((os) => {
          const qtdAjud = Number(os.quantidade_ajudante ?? os.qtd_ajudante ?? 0);
          const valAjud = Number(os.subtotal_viagem ?? (Number(os.valor_unit_ajud || 0) * qtdAjud) ?? 0);
          const totalOsVal = Number(os.total_os || 0);
          const tipoVeiculo = resolveTipoVeiculoDisplay(os);
          const valUnitVeic = Number(
            os.valor_unit_servi ??
            (qtdAjud > 0 ? (totalOsVal - valAjud) : totalOsVal) ??
            0
          );

          return {
            id: os.id,
            data_atendimento: os.data_atendimento,
            cliente_nome: os.cliente_nome || os.pedido?.pessoa?.nome || medicao.cliente_nome,
            motorista_nome: os.veiculos?.motorista?.nome || os.motorista?.nome || '-',
            placa: os.veiculos?.placa || os.placa || '-',
            tipo_veiculo: tipoVeiculo,
            descricao: os.descricao ? fixEncodingIssues(os.descricao) : '',
            valor_unit_servi: valUnitVeic,
            retirada: os.retirada || '',
            entrega: os.entrega || '',
            horario_inicio: os.horario_inicio || '',
            horario_final: os.horario_final || '',
            hora_total: os.hora_total || '',
            quantidade_ajudante: qtdAjud,
            valor_ajudante: valAjud,
            total_os: totalOsVal,
          };
        })
      );
    } catch (err: any) {
      console.error('Erro ao exportar medição na área do cliente:', err);
      alert('Erro ao gerar o arquivo Excel da medição: ' + (err?.message || 'Tente novamente.'));
    }
  };

  const handleExportPdf = async () => {
    if (osList.length === 0) {
      alert('Não há ordens de serviço nesta medição para exportar.');
      return;
    }
    try {
      setExportingPdf(true);
      await exportMedicaoToPdf(
        {
          id: medicao.id,
          type: 'cliente',
          clienteNome: medicao.cliente_nome || medicao.pedido?.pessoa?.nome,
          periodoInicial: medicao.periodo_inicial,
          periodoFinal: medicao.periodo_final,
          valorTotal: medicao.valor_medicao,
          statusNome: (medicao as any).status?.status || (medicao as any).status_nome,
        },
        osList.map((os) => ({
          id: os.id,
          data_atendimento: os.data_atendimento,
          cliente_nome: os.cliente_nome || os.pedido?.pessoa?.nome || medicao.cliente_nome,
          motorista_nome: os.veiculos?.motorista?.nome || os.motorista?.nome || '-',
          placa: os.veiculos?.placa || os.placa || '-',
          tipo_veiculo: os.veiculos?.tipo || os.tipo_veiculo || '-',
          descricao: os.descricao || '',
          retirada: os.retirada || '',
          entrega: os.entrega || '',
          total_os: Number(os.total_os) || 0,
        }))
      );
    } catch (err) {
      console.error('Erro ao exportar PDF:', err);
    } finally {
      setExportingPdf(false);
    }
  };

  const formatCurrency = (val?: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(val || 0);
  };

  const getStatusBadge = (statusId?: number) => {
    switch (statusId) {
      case 1:
        return (
          <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-300 flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Aprovado
          </span>
        );
      case 2:
        return (
          <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800 border border-red-300 flex items-center gap-1">
            <XCircle className="w-3.5 h-3.5 text-red-600" /> Reprovado
          </span>
        );
      case 3:
      default:
        return (
          <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800 border border-amber-300 flex items-center gap-1">
            <Clock className="w-3.5 h-3.5 text-amber-600" /> Em Análise
          </span>
        );
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-fade-in">
      <div className="bg-white rounded-3xl shadow-2xl border border-slate-200 w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 bg-blue-50 text-blue-600 rounded-2xl">
              <FileSpreadsheet className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h2 className="text-lg font-black text-slate-900">
                  Medição #{medicao.id}
                </h2>
                {getStatusBadge(medicao.status_medicao_id)}
              </div>
              <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-2">
                <span>Período:</span>
                <span className="font-semibold text-slate-700">
                  {formatDateBR(medicao.periodo_inicial)}
                  {' até '}
                  {formatDateBR(medicao.periodo_final)}
                </span>
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl">
              <span className="text-[11px] font-bold text-slate-400 uppercase">Valor da Medição</span>
              <p className="text-xl font-black text-slate-900 mt-1">
                {formatCurrency(medicao.valor_medicao || 0)}
              </p>
            </div>

            <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl">
              <span className="text-[11px] font-bold text-slate-400 uppercase">Ordens de Serviço</span>
              <p className="text-xl font-black text-blue-600 mt-1">
                {osList.length} OS(s) vinculada(s)
              </p>
            </div>

            <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl">
              <span className="text-[11px] font-bold text-slate-400 uppercase">Documento / Anexo</span>
              <div className="mt-1">
                {medicao.arquivo ? (
                  <a
                    href={medicao.arquivo}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-600 hover:underline"
                  >
                    <FileText className="w-4 h-4" />
                    <span>Ver Relatório Completo</span>
                  </a>
                ) : (
                  <span className="text-xs text-slate-400">Nenhum anexo externo</span>
                )}
              </div>
            </div>
          </div>

          {/* Service Orders Table */}
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center space-x-2">
                <Truck className="w-4 h-4 text-slate-600" />
                <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">
                  Ordens de Serviço Integrantes desta Medição
                </h3>
                <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                  {osList.length}
                </span>
              </div>

              {osList.length > 0 && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleExportExcel}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-emerald-50 text-emerald-700 border border-emerald-300 hover:border-emerald-400 rounded-lg text-xs font-bold transition-all shadow-2xs cursor-pointer"
                    title="Exportar para Excel"
                  >
                    <FileSpreadsheet className="h-3.5 w-3.5 text-emerald-600" />
                    <span>Excel</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleExportPdf}
                    disabled={exportingPdf}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-red-50 text-red-700 border border-red-300 hover:border-red-400 rounded-lg text-xs font-bold transition-all shadow-2xs cursor-pointer disabled:opacity-50"
                    title="Exportar para PDF"
                  >
                    {exportingPdf ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin text-red-600" />
                        <span>Gerando...</span>
                      </>
                    ) : (
                      <>
                        <FileText className="h-3.5 w-3.5 text-red-600" />
                        <span>PDF</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>

            {loading ? (
              <div className="py-12 text-center">
                <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                <p className="text-xs text-slate-400">Carregando ordens de serviço...</p>
              </div>
            ) : osList.length === 0 ? (
              <div className="p-6 text-center bg-slate-50 rounded-2xl border border-slate-200 text-slate-500 text-xs">
                Nenhuma Ordem de Serviço específica encontrada vinculada a esta medição (apurado de forma consolidada).
              </div>
            ) : (
              <div className="border border-slate-200 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider text-[10px]">
                      <tr>
                        <th className="py-3 px-4">Nº O.S</th>
                        <th className="py-3 px-4">Data</th>
                        <th className="py-3 px-4">Veículo / Placa</th>
                        <th className="py-3 px-4">Origem / Destino</th>
                        <th className="py-3 px-4 text-right">Valor Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {osList.map((os) => (
                        <tr key={os.id} className="hover:bg-slate-50/60 transition-colors">
                          <td className="py-3 px-4 font-black text-slate-900">
                            {os.id}
                          </td>
                          <td className="py-3 px-4 text-slate-700 whitespace-nowrap">
                            {formatDateBR(os.data_atendimento)}
                          </td>
                          <td className="py-3 px-4 text-slate-700">
                            <span className="font-semibold">{os.veiculos?.placa || os.placa || '-'}</span>
                            {os.veiculos?.motorista?.nome && (
                              <span className="block text-[10px] text-slate-400">{os.veiculos.motorista.nome}</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-slate-600 max-w-xs truncate">
                            <div className="truncate">
                              <span className="font-semibold text-slate-700">De:</span> {os.retirada || '-'}
                            </div>
                            <div className="truncate text-slate-500">
                              <span className="font-semibold text-slate-700">Para:</span> {os.entrega || '-'}
                            </div>
                          </td>
                          <td className="py-3 px-4 font-bold text-slate-900 text-right whitespace-nowrap">
                            {formatCurrency(os.total_os || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl transition-all"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
